import React, { useState } from "react";
import { Box, Grid, Typography, Stack } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { DashboardCards } from "./DashboardCards";
import { RecentApiCallTable } from "../table/RecentApiCallTable";
import { formatNumber } from "../../utils/constant";
import { Skeleton } from "@mui/material";

/* ---------------- MOCK DATA ---------------- */

const formatDay = (day) => {
  const map = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };

  return map[day] || day;
};

const ChartSkeleton = ({ height = 280 }) => (
  <Skeleton variant="rounded" height={height} animation="wave" />
);

const PieSkeleton = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    height={220}
  >
    <Skeleton variant="circular" width={160} height={160} />
  </Stack>
);

const PieLegendSkeleton = ({ items = 5 }) => (
  <Stack spacing={0.75} mt={2}>
    {Array.from({ length: items }).map((_, index) => (
      <Stack
        key={index}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton width={80} height={14} />
        </Stack>

        <Skeleton width={60} height={14} />
      </Stack>
    ))}
  </Stack>
);

/* ---------------- DASHBOARD CONTENT ---------------- */

export const DashboardContent = ({
  summaryLoading,
  dailyUsageLoading,
  apiWiseLoading,
  tableLoading,
  recentApiCall,
  totalRecentApiCallCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  apiWiseUsageData,
  dailyUsageTrendData,
  companyDashboardSummaryData,
  onApiNameClick,
  apiNameFilter,
}) => {
  const generateColors = (count) =>
    Array.from(
      { length: count },
      (_, i) => `hsl(${(i * 360) / count}, 70%, 50%)`
    );

  const [showAllApis, setShowAllApis] = useState(false);
  const MAX_VISIBLE = 5;
  const apiDistribution = apiWiseUsageData?.categories || [];
  const COLORS = generateColors(apiDistribution.length);
  const pieData = apiDistribution.map((item, index) => ({
    ...item,
    color: COLORS[index],
  }));

  const hasPieData =
    Array.isArray(pieData) &&
    pieData.length > 0 &&
    pieData.some((item) => Number(item.total_calls) > 0);
  const visibleApis = showAllApis ? pieData : pieData.slice(0, MAX_VISIBLE);

  return (
    <Box>
      {/* Top Cards */}
      <Box sx={{ mb: 4 }}>
        <DashboardCards
          loading={summaryLoading}
          companyDashboardSummaryData={companyDashboardSummaryData}
        />
      </Box>

      {/* Usage Analytics */}
      <Grid container spacing={4}>
        {/* Daily Usage Trend */}
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              bgcolor: "white",
              p: 3,
              borderRadius: 3,
              height: "100%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "2rem", fontWeight: 700 }}>
              Daily Usage Trend
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: "1.4rem", fontWeight: 400, mb: 2 }}>
              API calls over the past week
            </Typography>
            {dailyUsageLoading ? (
              <ChartSkeleton height={280} />
            ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dailyUsageTrendData?.graphData}>
                <XAxis dataKey="day" tickFormatter={formatDay} />
                <YAxis tickFormatter={(value) => formatNumber(value)} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 13,
                  }}
                  formatter={(value, name) => {
                    const labelMap = {
                      totalCalls: "Total Calls",
                      successCalls: "Success Calls",
                    };
                    return [
                      `${value.toLocaleString()}`,
                      labelMap[name] || name,
                    ];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="totalCalls"
                  stroke="#4f46e5"
                  fill="#c7d2fe"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="successCalls"
                  stroke="#10b981"
                  fill="#bbf7d0"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </Box>
        </Grid>

        {/* API-wise Usage */}
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              bgcolor: "white",
              p: 3,
              borderRadius: 3,
              height: "100%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "2rem", fontWeight: 700 }}>
              API-wise Usage
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: "1.4rem", fontWeight: 400, mb: 2 }}>
              Breakdown by API type
            </Typography>

            {/* ===== CHART / EMPTY STATE ===== */}
            <Box sx={{ width: "100%", height: 220 }}>
                {apiWiseLoading ? (
                  <PieSkeleton />
                ) : hasPieData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="total_calls"
                      nameKey="category_name"
                      innerRadius={70}
                      outerRadius={100}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.category_name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;

                        const { category_name, total_calls, total_credits } =
                          payload[0].payload;

                        return (
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: "#ffffff",
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.08)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#020817",
                                mb: 0.5,
                              }}
                            >
                              {category_name}
                            </Typography>

                            <Typography sx={{ fontSize: 12 }}>
                              API Calls: {formatNumber(total_calls)}
                            </Typography>

                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                              Credits: {formatNumber(total_credits)}
                            </Typography>
                          </Box>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Stack
                  sx={{ width: "100%", height: "100%" }}
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  spacing={1}
                >
                  <Typography fontSize={14} fontWeight={600} color="#64748b">
                    No API usage data
                  </Typography>
                  <Typography fontSize={13} color="#94a3b8">
                    Data will appear once APIs are used
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* ===== (ONLY IF DATA EXISTS) ===== */}
            {apiWiseLoading ? (
                <PieLegendSkeleton items={MAX_VISIBLE} />
              ) : hasPieData ? (
              <>
                <Stack spacing={0.5} mt={2}>
                  {visibleApis.map((item) => (
                    <Stack
                      key={item.category_name}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      {/* Left: dot + label */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: item.color,
                          }}
                        />
                        <Typography fontSize={14}>
                          {item.category_name}
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ minWidth: 140, justifyContent: "flex-end" }}
                      >
                        {/* Calls */}
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#020817",
                            minWidth: 50,
                            textAlign: "right",
                          }}
                        >
                          {formatNumber(item.total_calls)}
                        </Typography>

                        {/* Credits */}
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#64748b",
                            minWidth: 70,
                            textAlign: "right",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatNumber(item.total_credits)} credits
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>

                {/* See More / See Less */}
                {apiDistribution.length > MAX_VISIBLE && (
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "10px",
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#2563EB",
                      }}
                      onClick={() => setShowAllApis((prev) => !prev)}
                    >
                      {showAllApis ? "See less" : "See more..."}
                    </Typography>
                  </Box>
                )}
                </>
            ) : null}
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ mt: 1 }}> </Box>
    </Box>
  );
};
